import {GenericTypeDef} from "@core/model/generic-type-def.model";
import {Category, ScoringBreakdown} from "@core/model/retailer/application-form.model";
import {ControlType} from "@features/leads/application-form-builder/add-questions/question-components.model";
import {displayShortDate} from "@ui-kit/framework/util/static/date-format-utils";
import {displayUuid} from "@ui-kit/framework/util/static/string-format-util";
import {
  Boolean,
  Date,
  DisplayFunction,
  DisplayOrder,
  Number,
  Options,
  Percentage, SortDefault,
  String,
  TableDisplayed,
  TableDisplayedDefault,
  TableIdentifier
} from "@ui-kit/webkit/components/crud-item/crud-item-decorator";

export class AudienceCategoryOption extends GenericTypeDef {
}

export class SurveyStatusOption extends GenericTypeDef {
}

export class SubmissionTypeOption extends GenericTypeDef {
}


export enum AudienceCategory {
  GENERAL_PUBLIC = 'General public',
  MERCHANTS = 'Merchants',
}

export enum SurveyStatus {
  PUBLISHED = 'PUBLISHED',
  DRAFT = 'DRAFT',
}

export enum SubmissionType {
}


//todo move the initial value addigns to this class as an exported constant


export class Survey {
  @TableIdentifier
  @String('ID')
  @DisplayOrder(1)
  @DisplayFunction(displayUuid)
  uuid: string | null;

  @String('Offering ID')
  @DisplayOrder(2)
  @TableDisplayed
  offeringUuid: string | null;

  @String('Name')
  @DisplayOrder(6)
  @TableDisplayedDefault
  name: string | null;

  @String('DisplayTitle')
  displayTitle: string | null;

  @String('Code')
  @DisplayOrder(13)
  @TableDisplayed
  code: string | null;

  @String('Audience')
  @DisplayOrder(5)
  @TableDisplayedDefault
  @Options([{GENERAL_PUBLIC: 'GENERAL_PUBLIC'}, {MEMBER: 'MEMBER'}])
  audience: AudienceCategory | null;

  @String('Status')
  @DisplayOrder(7)
  @TableDisplayedDefault
  @Options([{PUBLISHED: 'PUBLISHED'}, {DRAFT: 'DRAFT'}])
  status: SurveyStatus | null;

  @Number('Responses')
  @DisplayOrder(8)
  @TableDisplayedDefault
  responses: number | null;

  @Number('Partially Completed')
  @DisplayOrder(9)
  @TableDisplayedDefault
  partiallyCompleted: number | null;

  @Number('Fully Completed')
  @DisplayOrder(10)
  @TableDisplayedDefault
  fullyCompleted: number | null;

  @Number('Overall Score')
  @DisplayOrder(11)
  @TableDisplayedDefault
  overallScore: number | null;

  @Boolean('Allow Multi Submission')
  @DisplayOrder(12)
  allowMultiSubmission: boolean | null;

  @Date('Start Date', 'yyyy-MM-dd HH:mm:ss')
  @TableDisplayedDefault
  @DisplayOrder(3)
  @DisplayFunction(displayShortDate)
  @SortDefault("desc")
  startDate: string | null;

  @Date('End Date', 'yyyy-MM-dd HH:mm:ss')
  @TableDisplayedDefault
  @DisplayOrder(4)
  @DisplayFunction(displayShortDate)
  endDate: string | null;

  payload: Category[];
  active: boolean | null;

  constructor(obj: any) {
     obj = obj || {};
    this.uuid = obj.uuid ?? null;
    this.offeringUuid = obj.offeringUuid ?? null;
    this.name = obj.name ?? null;
    this.displayTitle = obj.displayTitle ?? null;
    this.code = obj.code ?? null;
    this.audience = obj.audience ?? null;
    this.status = obj.status ?? null;
    this.responses = obj.responses ?? null;
    this.partiallyCompleted = obj.partiallyCompleted ?? null;
    this.fullyCompleted = obj.fullyCompleted ?? null;
    this.overallScore = obj.overallScore ?? null;
    this.allowMultiSubmission = obj.allowMultiSubmission ?? null;
    this.startDate = obj.startDate ?? null;
    this.endDate = obj.endDate ?? null;
    this.payload = obj.payload ?? [];
    this.active = obj.active ?? null;
  }
}

export class SurveySubmissionSummary {
  @TableIdentifier
  @String('ID')
  @DisplayOrder(1)
  @DisplayFunction(displayUuid)
  uuid: string | null;

  @Date('Response Date', 'yyyy-MM-dd')
  @TableDisplayedDefault
  @DisplayOrder(2)
  @DisplayFunction(displayShortDate)
  @SortDefault("desc")
  responseDate: string | null;

  @String('Email')
  @DisplayOrder(3)
  @TableDisplayedDefault
  email: string | null;

  @String('Questions Answered')
  @DisplayOrder(4)
  @TableDisplayedDefault
  questionsAnswered: string | null = null;

  questionsCompletedCount: number | null;
  questionsTotalCount: number | null;
  questionsPercentageAnswered: number | null;

  @String('Type')
  @DisplayOrder(5)
  @TableDisplayedDefault
  submissionType: string | null;

  @Percentage('Preferred & Passed')
  @DisplayOrder(6)
  @TableDisplayedDefault
  preferredAndPassed: number | null;

  @Percentage('1st Rating results')
  @DisplayOrder(7)
  @TableDisplayedDefault
  firstRating: number | null;

  @Percentage('2nd & 3rd Rating results')
  @DisplayOrder(8)
  @TableDisplayedDefault
  secondThirdRating: number | null;

  @Percentage('Overall Score')
  @DisplayOrder(9)
  @TableDisplayedDefault
  overallScore: number | null;

  constructor(obj: any) {
    obj = obj || {};
    this.uuid = obj.uuid ?? null;
    this.responseDate = obj.responseDate ?? null;
    this.email = obj.email ?? null;
    this.questionsCompletedCount = obj.questionsCompletedCount ?? null;
    this.questionsTotalCount = obj.questionsTotalCount ?? null;
    this.questionsPercentageAnswered = obj.questionsPercentageAnswered ?? null;
    this.submissionType = obj.submissionType ?? null;
    this.overallScore = obj.overallScore ?? null;
    this.preferredAndPassed = obj.preferredAndPassed ?? null;
    this.firstRating = obj.firstRating ?? null;
    this.secondThirdRating = obj.secondThirdRating ?? null;
    this.questionsAnswered = displayQuestionsAnswered(obj) ?? null;
  }
}

export interface SurveySubmission {
  uuid: string;
  offeringUuid: string;
  payload: Category[];
  complete: string;
  surveyTemplateCode: string;
  submitDate: Date;
  overallScore: number;
  scoringBreakdown: ScoringBreakdown;
}

export interface CreateSurveyRequest {
  name: string;
  displayTitle: string;
  audience: string;
  startDate: Date | string;
  endDate?: Date | string;
  allowMultiSubmission: string;
}

export interface DuplicateSurveyRequest {
  templateUuid: string;
  name?: string;
  displayTitle?: string;
  audience?: string;
  startDate?: Date;
  endDate?: Date;
}

export interface UpdateSurveyRequest {
  uuid: string;
  offeringUuid: string;
  name: string;
  displayTitle?: string;
  audience: AudienceCategory;
  status: SurveyStatus;
  startDate: string;
  endDate?: string | null;
  allowMultiSubmission: boolean;
  payload: Category[];
}

export interface SurveyStatisticsRequest {
  fromDate: Date;
  toDate: Date;
  formTemplateCode: string;
}

export interface QuestionStatsGroup {
  groupCode: string;
  questionStats: QuestionStats[]
}

export interface QuestionStats {
  questionUuid: string;
  questionCode: string;
  questionText: string;
  controlType: ControlType;
  options: QuestionStatOption[];
  totalResponses: number;
  displayOrder: number;
}

export interface QuestionStatOption {
  optionCode: string;
  optionText: string;
  responseCount: number;
}

function displayQuestionsAnswered(data: SurveySubmissionSummary): string | null {
  if (!data) return null;
  if (data.questionsCompletedCount == null && data.questionsTotalCount == null && data.questionsPercentageAnswered == null) return null;
  return `${data.questionsCompletedCount ?? 0} / ${data.questionsTotalCount ?? 0} - ${data.questionsPercentageAnswered ?? 0}%`;
}
